import { useEffect, useState } from 'react'
import { supabase } from '@/supabase-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Flame } from 'lucide-react'

const AUTO_VALUE = '__auto__'

export default function MoveToHeatCyclingDialog({
  isOpen,
  onClose,
  batch,
  onConfirm
}) {
  const [barrels, setBarrels] = useState([])
  const [barrelChoice, setBarrelChoice] = useState(AUTO_VALUE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setBarrelChoice(AUTO_VALUE)
    setError(null)
    supabase
      .from('barrels')
      .select('id, display_name')
      .eq('active', true)
      .order('id')
      .then(({ data, error: err }) => {
        if (err) {
          console.error('[MoveToHeatCyclingDialog] failed to load barrels:', err)
          setBarrels([])
        } else {
          setBarrels(data ?? [])
        }
      })
  }, [isOpen])

  const handleConfirm = async () => {
    setSaving(true)
    setError(null)
    try {
      const barrelId = barrelChoice === AUTO_VALUE ? null : barrelChoice
      await onConfirm({ barrelId })
      onClose()
    } catch (err) {
      console.error('[MoveToHeatCyclingDialog] confirm error:', err)
      setError(err.message || 'Failed to move batch to heat cycling')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-600" />
            Move to Heat Cycling
          </DialogTitle>
          <DialogDescription>
            Marks <span className="font-mono">{batch?.batch_code}</span> as in
            HEAT_CYCLING and stamps the start time. The detected sensor cycle
            will be auto-linked to this batch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="barrel">Barrel (optional)</Label>
            <Select value={barrelChoice} onValueChange={setBarrelChoice}>
              <SelectTrigger id="barrel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_VALUE}>
                  Auto-detect (single batch in cycle)
                </SelectItem>
                {barrels.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-content-tertiary">
              Only needed if both barrels are running at the same time.
            </p>
          </div>

          {error && (
            <p className="text-sm text-odin-red">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? 'Moving…' : 'Move to HEAT_CYCLING'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
